import { useState, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import apiClient from '@/api/client'
import type { VendorBill, VendorAgingRow, VendorLedgerEntry } from '@/types'
import { useCurrency } from '@/contexts/CurrencyContext'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { DatePicker } from '@/components/ui/date-picker'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog'

type ToastFn = (type: 'success' | 'error', message: string) => void

function billStatusVariant(status: string): 'secondary' | 'outline' | 'info' | 'warning' | 'success' | 'destructive' {
  switch (status) {
    case 'draft': return 'secondary'
    case 'open': return 'warning'
    case 'partially_paid': return 'info'
    case 'paid': return 'success'
    case 'void': return 'destructive'
    default: return 'outline'
  }
}

export function VendorAPTab({ showToast }: { showToast: ToastFn }) {
  const qc = useQueryClient()
  const { formatCurrency } = useCurrency()

  const { data: agingRes } = useQuery({ queryKey: ['vendorAging'], queryFn: () => apiClient.getVendorAging() })
  const aging: VendorAgingRow[] = agingRes?.data ?? []

  const { data: billsRes } = useQuery({ queryKey: ['vendorBills'], queryFn: () => apiClient.listVendorBills() })
  const bills: VendorBill[] = billsRes?.data ?? []

  const [ledgerSupplier, setLedgerSupplier] = useState<{ id: string; name: string } | null>(null)
  const [ledgerFrom, setLedgerFrom] = useState('')
  const [ledgerTo, setLedgerTo] = useState('')
  const [exportingLedger, setExportingLedger] = useState(false)
  const { data: ledgerRes } = useQuery({
    queryKey: ['vendorLedger', ledgerSupplier?.id],
    queryFn: () => apiClient.getVendorLedger(ledgerSupplier!.id),
    enabled: !!ledgerSupplier,
  })
  const ledger: VendorLedgerEntry[] = ledgerRes?.data ?? []

  // Open the per-vendor ledger with a sensible default export window
  // (current month to date). The on-screen ledger shows all-time; the export
  // button below produces a date-bounded .xlsx statement for the picked range.
  const openLedger = (id: string, name: string) => {
    const today = new Date()
    const first = new Date(today.getFullYear(), today.getMonth(), 1)
    const iso = (d: Date) =>
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
    setLedgerFrom(iso(first))
    setLedgerTo(iso(today))
    setLedgerSupplier({ id, name })
  }

  const exportLedger = async () => {
    if (!ledgerSupplier) return
    if (!ledgerFrom || !ledgerTo) {
      showToast('error', 'Pick a from and to date first.')
      return
    }
    setExportingLedger(true)
    try {
      await apiClient.exportVendorLedgerXlsx(ledgerSupplier.id, ledgerFrom, ledgerTo)
    } catch {
      showToast('error', 'Export failed. Please try again.')
    } finally {
      setExportingLedger(false)
    }
  }

  // Reconcile (draft → open) dialog
  const [reconcileBill, setReconcileBill] = useState<VendorBill | null>(null)
  const [recInvoice, setRecInvoice] = useState('')
  const [recBillDate, setRecBillDate] = useState('')
  const [recDueDate, setRecDueDate] = useState('')
  const [recAmount, setRecAmount] = useState('')

  // Payment dialog
  const [payBill, setPayBill] = useState<VendorBill | null>(null)
  const [payAmount, setPayAmount] = useState('')
  const [payMethod, setPayMethod] = useState<'cash' | 'bank'>('cash')
  const [payNote, setPayNote] = useState('')

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['vendorBills'] })
    qc.invalidateQueries({ queryKey: ['vendorAging'] })
    qc.invalidateQueries({ queryKey: ['vendorLedger'] })
  }

  const reconcile = useMutation({
    mutationFn: () => {
      if (!reconcileBill) throw new Error('No bill')
      return apiClient.updateVendorBill(reconcileBill.id, {
        invoice_number: recInvoice || undefined,
        bill_date: recBillDate || undefined,
        due_date: recDueDate || undefined,
        amount: recAmount ? Number(recAmount) : undefined,
      })
    },
    onSuccess: (res) => {
      if (!res.success) { showToast('error', res.error || res.message || 'Failed'); return }
      showToast('success', 'Bill updated')
      invalidate()
      setReconcileBill(null)
    },
    onError: (e: Error) => showToast('error', e.message || 'Failed'),
  })

  // B-P1: one stable idempotency key per open payment. Reused if the user
  // retries after a transient error (so the backend dedups instead of
  // double-paying); a fresh key is minted when a different bill's modal opens.
  const payKey = useMemo(() => (payBill ? crypto.randomUUID() : ''), [payBill?.id])

  const pay = useMutation({
    mutationFn: () => {
      if (!payBill) throw new Error('No bill')
      return apiClient.createVendorPayment({
        vendor_bill_id: payBill.id,
        amount: Number(payAmount),
        method: payMethod,
        note: payNote || undefined,
        idempotency_key: payKey || undefined,
      })
    },
    onSuccess: (res) => {
      if (!res.success) { showToast('error', res.error || res.message || 'Failed'); return }
      showToast('success', 'Payment recorded')
      invalidate()
      setPayBill(null)
      setPayAmount('')
      setPayNote('')
    },
    onError: (e: Error) => showToast('error', e.message || 'Failed'),
  })

  return (
    <div className="space-y-6 pb-12">
      {/* Aging */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-xl font-semibold">Vendor aging</CardTitle>
          <CardDescription className="text-base">Outstanding payables bucketed by days past the bill due date.</CardDescription>
        </CardHeader>
        <CardContent className="px-0 sm:px-6">
          {aging.length === 0 ? (
            <p className="px-6 text-base text-muted-foreground">No vendor bills yet.</p>
          ) : (
            <div className="overflow-x-auto border-t sm:rounded-md sm:border sm:mx-0">
              <table className="w-full min-w-[760px] text-left text-base">
                <thead className="bg-muted/50 border-b text-sm">
                  <tr>
                    <th className="px-4 py-3 font-semibold">Supplier</th>
                    <th className="px-4 py-3 font-semibold text-right">Outstanding</th>
                    <th className="px-4 py-3 font-semibold text-right">0–30</th>
                    <th className="px-4 py-3 font-semibold text-right">31–60</th>
                    <th className="px-4 py-3 font-semibold text-right">61–90</th>
                    <th className="px-4 py-3 font-semibold text-right">90+</th>
                    <th className="px-4 py-3 font-semibold text-center">Paid / Pending</th>
                    <th className="px-4 py-3" />
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {aging.map((a) => (
                    <tr key={a.supplier_id}>
                      <td className="px-4 py-3 font-medium">{a.supplier_name}</td>
                      <td className="px-4 py-3 text-right tabular-nums font-semibold">{formatCurrency(a.outstanding)}</td>
                      <td className="px-4 py-3 text-right tabular-nums">{formatCurrency(a.b_0_30)}</td>
                      <td className="px-4 py-3 text-right tabular-nums">{formatCurrency(a.b_31_60)}</td>
                      <td className="px-4 py-3 text-right tabular-nums">{formatCurrency(a.b_61_90)}</td>
                      <td className="px-4 py-3 text-right tabular-nums text-red-600">{formatCurrency(a.b_90_plus)}</td>
                      <td className="px-4 py-3 text-center text-sm text-muted-foreground">{a.paid_count} / {a.pending_count}</td>
                      <td className="px-4 py-3 text-right">
                        <Button size="sm" variant="ghost" className="h-8 text-sm" onClick={() => openLedger(a.supplier_id, a.supplier_name)}>
                          Ledger
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Bills */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-xl font-semibold">Vendor bills</CardTitle>
          <CardDescription className="text-base">Bills are auto-drafted when stock is received; reconcile with the invoice, then record payments.</CardDescription>
        </CardHeader>
        <CardContent className="px-0 sm:px-6">
          {bills.length === 0 ? (
            <p className="px-6 text-base text-muted-foreground">No vendor bills yet.</p>
          ) : (
            <div className="overflow-x-auto border-t sm:rounded-md sm:border sm:mx-0">
              <table className="w-full min-w-[820px] text-left text-base">
                <thead className="bg-muted/50 border-b text-sm">
                  <tr>
                    <th className="px-4 py-3 font-semibold">Supplier</th>
                    <th className="px-4 py-3 font-semibold">Invoice</th>
                    <th className="px-4 py-3 font-semibold">Due</th>
                    <th className="px-4 py-3 font-semibold text-right">Amount</th>
                    <th className="px-4 py-3 font-semibold text-right">Outstanding</th>
                    <th className="px-4 py-3 font-semibold">Status</th>
                    <th className="px-4 py-3" />
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {bills.map((b) => (
                    <tr key={b.id}>
                      <td className="px-4 py-3 font-medium">{b.supplier_name}</td>
                      <td className="px-4 py-3 text-sm">{b.invoice_number || <span className="text-muted-foreground">—</span>}</td>
                      <td className="px-4 py-3 text-sm">{b.due_date || '—'}</td>
                      <td className="px-4 py-3 text-right tabular-nums">{formatCurrency(b.amount)}</td>
                      <td className="px-4 py-3 text-right tabular-nums font-semibold">{formatCurrency(b.outstanding)}</td>
                      <td className="px-4 py-3"><Badge variant={billStatusVariant(b.status)}>{b.status.replace(/_/g, ' ')}</Badge></td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex justify-end gap-2">
                          {b.status === 'draft' && (
                            <Button size="sm" variant="outline" className="h-8 text-sm" onClick={() => {
                              setReconcileBill(b)
                              setRecInvoice(b.invoice_number || '')
                              setRecBillDate(b.bill_date || '')
                              setRecDueDate(b.due_date || '')
                              setRecAmount(String(b.amount))
                            }}>
                              Reconcile
                            </Button>
                          )}
                          {(b.status === 'open' || b.status === 'partially_paid') && (
                            <Button size="sm" variant="secondary" className="h-8 text-sm" onClick={() => {
                              setPayBill(b)
                              setPayAmount(String(b.outstanding))
                              setPayMethod('cash')
                            }}>
                              Record payment
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Ledger drill-down */}
      <Dialog open={!!ledgerSupplier} onOpenChange={(o) => !o && setLedgerSupplier(null)}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl">Ledger — {ledgerSupplier?.name}</DialogTitle>
            <DialogDescription className="text-base">Bills (debit), payments &amp; returns (credit), with running balance.</DialogDescription>
          </DialogHeader>

          {/* Export a date-bounded .xlsx statement (ledger + items purchased + payments). */}
          <div className="flex flex-wrap items-end gap-3 rounded-md border bg-muted/30 px-3 py-3">
            <div className="space-y-1">
              <Label className="text-sm">From</Label>
              <DatePicker className="h-10 text-sm" value={ledgerFrom} onChange={setLedgerFrom} />
            </div>
            <div className="space-y-1">
              <Label className="text-sm">To</Label>
              <DatePicker className="h-10 text-sm" value={ledgerTo} onChange={setLedgerTo} />
            </div>
            <Button
              variant="outline"
              className="h-10 text-sm"
              disabled={exportingLedger}
              onClick={exportLedger}
            >
              {exportingLedger ? 'Exporting…' : 'Export statement (.xlsx)'}
            </Button>
          </div>

          {ledger.length === 0 ? (
            <p className="text-base text-muted-foreground">No ledger entries.</p>
          ) : (
            <table className="w-full text-left text-base">
              <thead className="bg-muted/50 border-b text-sm">
                <tr>
                  <th className="px-3 py-2 font-semibold">Date</th>
                  <th className="px-3 py-2 font-semibold">Type</th>
                  <th className="px-3 py-2 font-semibold">Description</th>
                  <th className="px-3 py-2 font-semibold text-right">Debit</th>
                  <th className="px-3 py-2 font-semibold text-right">Credit</th>
                  <th className="px-3 py-2 font-semibold text-right">Balance</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {ledger.map((e, i) => (
                  <tr key={i}>
                    <td className="px-3 py-2 text-sm">{e.date}</td>
                    <td className="px-3 py-2 text-sm capitalize">{e.type}</td>
                    <td className="px-3 py-2 text-sm">{e.description}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{e.debit ? formatCurrency(e.debit) : '—'}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{e.credit ? formatCurrency(e.credit) : '—'}</td>
                    <td className="px-3 py-2 text-right tabular-nums font-semibold">{formatCurrency(e.balance)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </DialogContent>
      </Dialog>

      {/* Reconcile dialog */}
      <Dialog open={!!reconcileBill} onOpenChange={(o) => !o && setReconcileBill(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl">Reconcile bill</DialogTitle>
            <DialogDescription className="text-base">Enter the vendor's invoice details. This moves the bill to open so it can be paid.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-base">Invoice number</Label>
              <Input className="h-11 text-base" value={recInvoice} onChange={(e) => setRecInvoice(e.target.value)} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="text-base">Bill date</Label>
                <DatePicker className="h-11 text-base" value={recBillDate} onChange={setRecBillDate} />
              </div>
              <div className="space-y-2">
                <Label className="text-base">Due date</Label>
                <DatePicker className="h-11 text-base" value={recDueDate} onChange={setRecDueDate} />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-base">Amount</Label>
              <Input type="number" step="0.01" className="h-11 text-base tabular-nums" value={recAmount} onChange={(e) => setRecAmount(e.target.value)} />
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" className="h-11 text-base" onClick={() => setReconcileBill(null)}>Cancel</Button>
            <Button className="h-11 text-base" disabled={reconcile.isPending} onClick={() => reconcile.mutate()}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payment dialog */}
      <Dialog open={!!payBill} onOpenChange={(o) => !o && setPayBill(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl">Record payment</DialogTitle>
            <DialogDescription className="text-base">
              {payBill?.supplier_name} · outstanding {payBill ? formatCurrency(payBill.outstanding) : ''}. A cash payment posts to the till.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-base">Amount</Label>
              <Input type="number" step="0.01" className="h-11 text-base tabular-nums" value={payAmount} onChange={(e) => setPayAmount(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label className="text-base">Method</Label>
              <div className="flex gap-2">
                <Button type="button" variant={payMethod === 'cash' ? 'secondary' : 'outline'} className="h-10 text-sm" onClick={() => setPayMethod('cash')}>Cash</Button>
                <Button type="button" variant={payMethod === 'bank' ? 'secondary' : 'outline'} className="h-10 text-sm" onClick={() => setPayMethod('bank')}>Bank</Button>
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-base">Note</Label>
              <Input className="h-11 text-base" value={payNote} onChange={(e) => setPayNote(e.target.value)} placeholder="Optional reference…" />
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" className="h-11 text-base" onClick={() => setPayBill(null)}>Cancel</Button>
            <Button className="h-11 text-base" disabled={pay.isPending || !payAmount || Number(payAmount) <= 0} onClick={() => pay.mutate()}>Record payment</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
